import React from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { OverviewTab } from "@/components/crm/OverviewTab";
import { LeadsTab } from "@/components/crm/LeadsTab";
import { FollowUpsTab } from "@/components/crm/FollowUpsTab";
import { SettingsTab } from "@/components/crm/SettingsTab";
import { LogOut, ExternalLink } from "lucide-react";

export default function CRMDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/crm/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[color:var(--hs-bg-0)] text-white" data-testid="crm-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[color:rgba(11,12,14,0.85)] backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Holdstill Records" className="h-8 w-auto" />
            <span className="hidden sm:inline font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[color:var(--hs-lime)] border border-[color:rgba(202,255,26,0.3)] rounded-full px-2.5 py-1">
              CRM
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/"
              className="hidden sm:flex items-center gap-1.5 text-xs text-white/50 hover:text-[color:var(--hs-lime)]"
              data-testid="crm-view-site-link"
            >
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </Link>
            <div className="flex items-center gap-2.5">
              <Avatar className="h-8 w-8 border border-white/20">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[color:var(--hs-bg-2)] text-[color:var(--hs-lime)] text-xs">
                  {(user?.name || "T").charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="hidden md:inline text-sm text-white/80" data-testid="crm-user-name">
                {user?.name}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="crm-logout-button"
              className="text-white/60 hover:text-white hover:bg-white/10 h-9"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="bg-[color:var(--hs-bg-1)] border border-white/10 rounded-xl h-11 p-1 mb-6 flex-wrap">
            <TabsTrigger value="overview" data-testid="crm-tab-overview" className="rounded-lg data-[state=active]:bg-[color:var(--hs-lime)] data-[state=active]:text-[color:var(--hs-bg-0)] text-white/60 px-4">
              Overview
            </TabsTrigger>
            <TabsTrigger value="leads" data-testid="crm-tab-leads" className="rounded-lg data-[state=active]:bg-[color:var(--hs-lime)] data-[state=active]:text-[color:var(--hs-bg-0)] text-white/60 px-4">
              Leads
            </TabsTrigger>
            <TabsTrigger value="followups" data-testid="crm-tab-followups" className="rounded-lg data-[state=active]:bg-[color:var(--hs-lime)] data-[state=active]:text-[color:var(--hs-bg-0)] text-white/60 px-4">
              Follow-ups
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="crm-tab-settings" className="rounded-lg data-[state=active]:bg-[color:var(--hs-lime)] data-[state=active]:text-[color:var(--hs-bg-0)] text-white/60 px-4">
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab />
          </TabsContent>
          <TabsContent value="leads">
            <LeadsTab />
          </TabsContent>
          <TabsContent value="followups">
            <FollowUpsTab />
          </TabsContent>
          <TabsContent value="settings">
            <SettingsTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
